import React, { useState, useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { RESERVE } from "@/constants/testIds";
import { Logo } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { trackCtaClick } from "@/lib/analytics";

export default function ReservationDialog({ artwork, open, onClose, artworks }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [artworkId, setArtworkId] = useState(artwork?.id || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [payUrl, setPayUrl] = useState("");
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api.get("/patronage-tiers").then((r) => setTiers(r.data || [])).catch(() => {});
  }, []);

  const selectedArt = useMemo(() => {
    return (artworks || []).find((a) => a.id === artworkId);
  }, [artworks, artworkId]);

  const currentTier = useMemo(() => {
    const p = selectedArt?.price;
    if (p == null || !tiers.length) return null;
    return (
      tiers.find((t) => p >= t.min && p <= t.max) ||
      (p > tiers[tiers.length - 1].max ? tiers[tiers.length - 1] : null)
    );
  }, [selectedArt, tiers]);

  useEffect(() => {
    if (open) {
      setArtworkId(artwork?.id || artworks?.[0]?.id || "");
      setDone(false);
      setPayUrl("");
    }
  }, [open, artwork, artworks]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!artworkId) {
      toast.error("Please choose an artwork.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reservations", {
        buyer_name: name,
        buyer_email: email,
        buyer_phone: phone || null,
        artwork_id: artworkId,
        message: message || null,
      });
      trackCtaClick("reservation_submitted");
      setPayUrl(data?.escrow_pay_url || "");
      setDone(true);
      toast.success("Reservation received.");
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) || "Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={RESERVE.dialog}
      className="fixed inset-0 z-[110] fade-in"
      style={{
        background: "rgba(6,10,8,0.7)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
      }}
      onClick={onClose}
    >
      <div className="min-h-full flex items-center justify-center p-4">
        <div
          className="relative bg-[var(--ivory)] w-full max-w-2xl border border-[#D4AF37]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            aria-label="Close"
            onClick={onClose}
            data-testid={RESERVE.cancel}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#1A1A1A]/60 hover:text-[#722F37]"
          >
            <X size={20} strokeWidth={1.5} />
          </button>

          <div className="px-8 md:px-12 py-10">
            <div className="text-center">
              <Logo size={54} />
              <div className="mt-4 text-[10px] tracking-[0.5em] uppercase text-[#722F37]">
                Inquire · Reserve
              </div>
              <h3 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">
                Place your reservation
              </h3>
              <p className="mt-3 text-sm text-[#1A1A1A]/70 max-w-md mx-auto">
                We will confirm your reservation and route you to Escrow.com
                to complete secure payment. Funds are held by Escrow.com until
                you have received your piece.
              </p>
            </div>

            {done ? (
              <div
                data-testid={RESERVE.success}
                className="mt-10 text-center reveal-up"
              >
                <div className="font-serif text-2xl text-[#0B3B24]">
                  Reservation received.
                </div>
                <p className="mt-4 text-sm text-[#1A1A1A]/70 max-w-md mx-auto">
                  A confirmation with your Certificate of Authenticity and
                  Letter of Appreciation has been sent to your email.
                </p>
                {payUrl ? (
                  <div className="mt-8 flex flex-col items-center gap-4">
                    <a
                      href={payUrl}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="reserve-pay-link"
                      className="btn-gold h-12 px-10 tracking-[0.25em] text-[11px] uppercase font-medium inline-flex items-center gap-2"
                    >
                      Complete secure payment on Escrow.com →
                    </a>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#1A1A1A]/50">
                      Secured by Escrow.com · your funds are protected
                    </div>
                    <button
                      onClick={onClose}
                      className="text-[10px] tracking-[0.3em] uppercase text-[#1A1A1A]/50 hover:text-[#722F37] mt-2"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <div className="mt-8">
                    <p className="text-sm text-[#722F37] max-w-md mx-auto">
                      A secured Escrow.com payment link will be emailed to you
                      shortly. Edgar Hicks will follow up personally.
                    </p>
                    <button
                      onClick={onClose}
                      className="mt-6 btn-gold h-11 px-8 tracking-[0.25em] text-[10px] uppercase"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-5">
                {currentTier && (
                  <div
                    data-testid="reserve-tier-preview"
                    className="border border-[#D4AF37] p-4 bg-[rgba(212,175,55,0.06)]"
                  >
                    <div className="text-[9px] tracking-[0.4em] uppercase text-[#722F37]">
                      Your patronage level
                    </div>
                    <div className="mt-1 flex items-baseline justify-between gap-4">
                      <div className="font-serif text-xl text-[#0B3B24]">
                        {currentTier.name}
                      </div>
                      <div className="font-serif text-xs text-[#1A1A1A]/60 italic">
                        {currentTier.tagline}
                      </div>
                    </div>
                    <div className="mt-2 text-[11px] text-[#1A1A1A]/70">
                      Includes: {currentTier.gifts.slice(0, 2).join(" · ")}
                      {currentTier.gifts.length > 2 && ` +${currentTier.gifts.length - 2} more`}
                    </div>
                  </div>
                )}
                <Field label="Full name">
                  <input
                    required
                    data-testid={RESERVE.name}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="editorial-input"
                    placeholder="Jane Collector"
                  />
                </Field>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Email">
                    <input
                      required
                      type="email"
                      data-testid={RESERVE.email}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="editorial-input"
                      placeholder="jane@example.com"
                    />
                  </Field>
                  <Field label="Phone (optional)">
                    <input
                      data-testid={RESERVE.phone}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="editorial-input"
                      placeholder="+1 555 000 0000"
                    />
                  </Field>
                </div>
                <Field label="Artwork of interest">
                  <select
                    value={artworkId}
                    onChange={(e) => setArtworkId(e.target.value)}
                    className="editorial-input bg-transparent"
                    required
                  >
                    {(artworks || []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.title} — ${a.price.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Message (optional)">
                  <textarea
                    data-testid={RESERVE.message}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    className="editorial-input resize-none"
                    placeholder="Anything the coordinator should know…"
                  />
                </Field>

                <button
                  type="submit"
                  data-testid={RESERVE.submit}
                  disabled={submitting}
                  className="w-full btn-gold h-12 tracking-[0.25em] text-[11px] uppercase font-medium mt-2"
                >
                  {submitting ? "Reserving & preparing checkout…" : "Reserve & continue to Escrow.com"}
                </button>

                <p className="text-[10px] tracking-[0.3em] uppercase text-[#1A1A1A]/50 text-center pt-2">
                  Secured by Escrow.com · you will be routed to secure checkout
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .editorial-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(26,26,26,0.25);
          padding: 8px 2px;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          color: #1A1A1A;
          outline: none;
          transition: border-color 200ms ease;
        }
        .editorial-input:focus {
          border-bottom-color: #D4AF37;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.35em] uppercase text-[#1A1A1A]/60 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
